import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase-client';
import { useToast } from '@/components/ui/use-toast';
import { format, addDays, isSameDay } from 'date-fns';
// import { supabase } from '../lib/supabase-client'; // Removed unused import

// Define the input data structure for creating an appointment
export interface ScheduleAppointmentData {
  slotId: string; // ID of the selected AppointmentSlot
  patientId: string; // ID of the patient
  doctorId: string; // ID of the doctor from the slot
  facilityId: string; // ID of the facility from the slot
  appointmentType: string; // Type from the slot
  reason?: string; // Optional reason provided by patient
  notes?: string; // Optional notes
}

// Types
export interface Provider {
  id: string;
  name: string;
  specialty?: string;
  imageUrl?: string;
  locationId: string; // Location where this provider works
}

export interface Location {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone?: string;
  imageUrl?: string;
}

export interface TimeSlot {
  id: string;
  providerId: string;
  locationId: string;
  startTime: string;
  endTime: string;
  available: boolean;
}

export interface AppointmentData {
  id?: string;
  patientId: string;
  providerId: string;
  locationId: string;
  appointmentTime: string;
  endTime: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no-show';
  testOrderId?: string;
  notes?: string;
}

interface ScheduleAppointmentOptions {
  testOrderId?: string; // Optional test order ID to link with the appointment
}


/**
 * Hook to provide appointment scheduling mutation.
 */
export const useScheduleAppointment = (options: ScheduleAppointmentOptions = {}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [selectedTimeSlotId, setSelectedTimeSlotId] = useState<string | null>(null);
  const [appointmentNotes, setAppointmentNotes] = useState<string>('');
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query for providers
  const {
    data: providers = [],
    isLoading: isLoadingProviders,
    error: providersError
  } = useQuery<Provider[]>({
    queryKey: ['providers'],
    queryFn: async () => {
      // Set to true to always use mock data while tables are being created
      const USE_MOCK_DATA = true;
      
      if (USE_MOCK_DATA) {
        return getMockProviders();
      }
      
      try {
        // Fetch real providers from clinic_specialists table
        const { data, error } = await supabase
          .from('clinic_specialists')
          .select('*')
          .eq('active', true)
          .order('display_name', { ascending: true });

        if (error) throw error;

        // Transform clinic_specialists columns to the Provider interface
        return data?.length ? data.map((specialist: { id: string; display_name?: string | null; role?: string | null; base_id?: string | null }) => ({
          id: specialist.id,
          name: specialist.display_name ?? 'Unknown Provider',
          specialty: specialist.role ?? 'General Medicine',
          imageUrl: undefined,
          locationId: specialist.base_id ?? 'main-hospital',
        })) : getMockProviders();
      } catch (error) {
        console.error('Error fetching providers:', error);
        return getMockProviders();
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Query for locations
  const {
    data: locations = [],
    isLoading: isLoadingLocations,
    error: locationsError
  } = useQuery<Location[]>({
    queryKey: ['locations'],
    queryFn: async () => {
      // Set to true to always use mock data while tables are being created
      const USE_MOCK_DATA = true;
      
      if (USE_MOCK_DATA) {
        return getMockLocations();
      }
      
      try {
        // caf_bases is the correct table for locations
        const { data, error } = await supabase
          .from('caf_bases')
          .select('*')
          .order('id', { ascending: true });

        if (error) throw error;
        return data?.length ? data as Location[] : getMockLocations();
      } catch (error) {
        console.error('Error fetching locations:', error);
        return getMockLocations();
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Query for available time slots based on selected date, provider, and location
  const {
    data: timeSlots = [],
    isLoading: isLoadingTimeSlots,
    error: timeSlotsError,
    refetch: refetchTimeSlots
  } = useQuery<TimeSlot[]>({
    queryKey: ['timeSlots', selectedDate, selectedProviderId, selectedLocationId],
    queryFn: async () => {
      if (!selectedProviderId || !selectedLocationId) return [];
      
      // Set to true to always use mock data while tables are being created
      const USE_MOCK_DATA = true;
      
      if (USE_MOCK_DATA) {
        return getMockTimeSlots();
      }
      
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      console.log(`Fetching slots for ${selectedProviderId} at ${selectedLocationId} on ${selectedDate}`);
      
      try {
        // clinic_slots schema: id, specialty, specialist_id, starts_at, ends_at,
        // capacity, booked_count, status, location
        const { data, error } = await supabase
          .from('clinic_slots')
          .select('*')
          .eq('specialist_id', selectedProviderId)
          .gte('starts_at', `${formattedDate}T00:00:00`)
          .lte('starts_at', `${formattedDate}T23:59:59`)
          .neq('status', 'cancelled');

        if (error) throw error;

        if (!data?.length) return getMockTimeSlots();

        // Map clinic_slots columns to the TimeSlot interface
        return data.map((slot: {
          id: string;
          specialist_id?: string | null;
          location?: string | null;
          starts_at?: string | null;
          ends_at?: string | null;
          capacity?: number | null;
          booked_count?: number | null;
          status?: string | null;
        }): TimeSlot => ({
          id: slot.id,
          providerId: slot.specialist_id ?? selectedProviderId ?? '',
          locationId: slot.location ?? selectedLocationId ?? '',
          startTime: slot.starts_at ?? '',
          endTime: slot.ends_at ?? '',
          available: (slot.capacity ?? 1) > (slot.booked_count ?? 0) && slot.status !== 'full',
        }));
      } catch (error) {
        console.error('Error fetching time slots:', error);
        return getMockTimeSlots();
      }
    },
    enabled: !!(selectedProviderId && selectedLocationId),
  });

  // Appointment creation mutation
  const scheduleMutation = useMutation({
    mutationFn: async (appointmentData: Omit<AppointmentData, 'id' | 'patientId'>) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      
      const patientId = session.user.id;
      
      // Find the selected location to get its name
      const selectedLocation = locations.find(loc => loc.id === appointmentData.locationId);
      const locationName = selectedLocation ? selectedLocation.name : 'Unknown Location';

      // Map to appointment_bookings column names
      const newAppointment = {
        user_id: patientId,                                           // TEXT column — store as string
        provider_id: appointmentData.providerId,
        clinic_location: locationName,
        preferred_date: appointmentData.appointmentTime.split('T')[0], // YYYY-MM-DD
        preferred_time: appointmentData.appointmentTime.split('T')[1], // HH:MM:SS
        appointment_type: 'Regular Checkup',
        status: 'pending',
        reason: appointmentData.notes ?? null,
        notes: options.testOrderId ? `test_order_id:${options.testOrderId}` : null,
      };

      // Log the object we're sending to help debug
      console.log('Scheduling appointment with data:', newAppointment);

      const { data, error } = await supabase
        .from('appointment_bookings')
        .insert(newAppointment)
        .select()
        .single();
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast({
        title: 'Appointment Scheduled',
        description: 'Your appointment has been successfully scheduled.',
      });
      navigate('/appointments');
    },
    onError: (error) => {
      console.error('Failed to schedule appointment:', error);
      toast({
        title: 'Scheduling Failed',
        description: 'There was a problem scheduling your appointment. Please try again.',
        variant: 'destructive',
      });
    }
  });

  // Get selected provider, location, and time slot objects
  const selectedProvider = providers.find(p => p.id === selectedProviderId) || null;
  const selectedLocation = locations.find(l => l.id === selectedLocationId) || null;
  const selectedTimeSlot = timeSlots.find(t => t.id === selectedTimeSlotId) || null;

  // Handle date selection - Now accepts Date | undefined
  const selectDate = useCallback((date: Date | undefined) => {
    // Only update state if a valid date is provided
    if (date) {
      const currentDate = selectedDate;
      setSelectedDate(date);
      // Clear time slot selection only if the day actually changed
      if (!currentDate || !isSameDay(date, currentDate)) {
        setSelectedTimeSlotId(null);
      }
    } else {
      // Handle case where date is deselected (optional, depends on UI needs)
      // Maybe set to today or keep the previous date?
      // For now, we'll just not update if undefined
      console.log("Date selection cleared or invalid");
    }
  }, [selectedDate]); // Dependency remains selectedDate

  // Handle provider selection
  const selectProvider = useCallback((providerId: string) => {
    setSelectedProviderId(providerId);
    
    // Find the provider's associated location and set it automatically
    const provider = providers.find(p => p.id === providerId);
    if (provider && provider.locationId) {
      setSelectedLocationId(provider.locationId);
    } else {
      setSelectedLocationId(null);
    }
    
    // Reset time slot when provider changes
    setSelectedTimeSlotId(null);
  }, [providers]); // Add providers as a dependency

  // Handle location selection
  const selectLocation = useCallback((locationId: string) => {
    setSelectedLocationId(locationId);
    setSelectedTimeSlotId(null); // Reset time slot when location changes
  }, []);

  // Handle time slot selection
  const selectTimeSlot = useCallback((timeSlotId: string) => {
    setSelectedTimeSlotId(timeSlotId);
  }, []);

  // Schedule appointment
  const scheduleAppointment = useCallback(() => {
    if (!selectedTimeSlot) {
      toast({
        title: 'Time Slot Required',
        description: 'Please select a time slot for your appointment.',
        variant: 'destructive',
      });
      return;
    }

    scheduleMutation.mutate({
      providerId: selectedTimeSlot.providerId,
      locationId: selectedTimeSlot.locationId,
      appointmentTime: selectedTimeSlot.startTime,
      endTime: selectedTimeSlot.endTime,
      status: 'scheduled',
      testOrderId: options.testOrderId,
      notes: appointmentNotes,
    });
  }, [selectedTimeSlot, appointmentNotes, options.testOrderId, scheduleMutation, toast]);

  // Generate a range of dates for the date picker
  const dateRange = useCallback(() => {
    const dates: Date[] = [];
    const startDate = new Date();
    
    // Generate dates for the next 14 days
    for (let i = 0; i < 14; i++) {
      dates.push(addDays(startDate, i));
    }
    
    return dates;
  }, []);

  // Helper function to get mock providers with actual database IDs
  function getMockProviders(): Provider[] {
    return [
      { 
        id: 'c7e83a36-f45d-4c1b-8ac7-e4cd901a2548', 
        name: 'Dr. David Johnson', 
        specialty: 'Cardiology',
        locationId: 'main-hospital'
      },
      { 
        id: '6f089f86-b61b-426f-b812-dc5170a973f2', 
        name: 'Dr. Sarah Smith', 
        specialty: 'Family Medicine',
        locationId: 'downtown-clinic'
      },
      { 
        id: '6cb2b777-86d9-4280-84bd-4f4cbb57a5ef', 
        name: 'Dr. Emily Parker', 
        specialty: 'Registered Nurse',
        locationId: 'ottawa-center'
      },
      { 
        id: '00000000-0000-0000-0000-000000000001', 
        name: 'Dr. General Provider', 
        specialty: 'Family Medicine',
        locationId: 'downtown-clinic'
      }
    ];
  }

  // Helper function to get mock locations
  function getMockLocations(): Location[] {
    return [
      { 
        id: 'main-hospital', 
        name: 'Main Hospital', 
        address: '123 Healthcare Blvd', 
        city: 'Metropolis', 
        state: 'NY', 
        zipCode: '10001', 
        phone: '555-123-4567' 
      },
      { 
        id: 'downtown-clinic', 
        name: 'Downtown Clinic', 
        address: '456 Medical Ave', 
        city: 'Metropolis', 
        state: 'NY', 
        zipCode: '10002', 
        phone: '555-987-6543' 
      },
      { 
        id: 'ottawa-center', 
        name: 'Ottawa Medical Center', 
        address: '789 Ottawa St', 
        city: 'Ottawa', 
        state: 'ON', 
        zipCode: 'K1P 1J1', 
        phone: '613-555-1234' 
      },
    ];
  }

  // Helper function to get mock time slots
  function getMockTimeSlots(): TimeSlot[] {
    const mockSlots: TimeSlot[] = [];
    const baseHour = 9; // Start at 9 AM
    
    // Generate mock slots every 30 minutes from 9 AM to 5 PM
    for (let hour = baseHour; hour < 17; hour++) {
      for (let minute of [0, 30]) {
        const startDate = new Date(selectedDate);
        startDate.setHours(hour, minute, 0, 0);
        
        const endDate = new Date(startDate);
        endDate.setMinutes(endDate.getMinutes() + 30);
        
        // Default provider and location if none selected
        const defaultProviderId = 'c7e83a36-f45d-4c1b-8ac7-e4cd901a2548'; // Use actual provider ID
        const defaultLocationId = 'main-hospital'; // Use string ID
        
        mockSlots.push({
          id: `slot-${hour}-${minute}-${Date.now()}`, // Make ID unique with timestamp
          providerId: selectedProviderId || defaultProviderId,
          locationId: selectedLocationId || defaultLocationId,
          startTime: startDate.toISOString(),
          endTime: endDate.toISOString(),
          available: Math.random() > 0.3 // 70% chance of being available
        });
      }
    }
    
    return mockSlots.filter(slot => slot.available);
  }

  return {
    // Data
    providers,
    locations,
    timeSlots,
    dateRange: dateRange(),
    
    // Selections
    selectedDate,
    selectedProviderId,
    selectedLocationId,
    selectedTimeSlotId,
    selectedProvider,
    selectedLocation,
    selectedTimeSlot,
    appointmentNotes,
    
    // Setters
    selectDate,
    selectProvider,
    selectLocation,
    selectTimeSlot,
    setAppointmentNotes,
    
    // Actions
    scheduleAppointment,
    refetchTimeSlots,
    
    // Status
    isLoadingProviders,
    isLoadingLocations,
    isLoadingTimeSlots,
    isScheduling: scheduleMutation.isPending,
    
    // Errors
    providersError,
    locationsError,
    timeSlotsError,
    schedulingError: scheduleMutation.error,
  };
}; 